import { Box, CircularProgress, Skeleton, Typography } from '@mui/material';
import { ConfirmationModal } from 'components/shared/CustomViewTask/components/ConfirmationModal';
import CustomViewTask from 'components/shared/CustomViewTask/CustomViewTask';
import InfiniteScrollColumn from 'components/shared/InfiniteScrollColumn/InfiniteScrollColumn';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TaskService } from 'service/task.service';
import zeplinColor from 'theme/Color';
import { TaskCard } from '../TaskCard';
import styles from './TasksKanbanColumn.module.css';

type TaskData = {
  totalRecords: number;
  records: any[];
  totalFilteredRecords: number;
};

type TasksKanbanColumnProps = {
  data: TaskData;
  loading: boolean;
  getTask: (next?: boolean) => Promise<void>;
  tabTitle: string;
  ellipse: string;
  setAction: React.Dispatch<React.SetStateAction<string>>;
  setEdit: React.Dispatch<React.SetStateAction<boolean>>;
  setViewDrawerState: React.Dispatch<React.SetStateAction<boolean>>;
  viewDrawerState: boolean;
  onAddTask?: () => void;
  onStatusChange?: () => void;
  onDrag?: (e: React.DragEvent, taskId: string, sourceColumn: string) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, targetColumn: string) => void;
};

type Assignee = {
  Name: string;
  Id: string;
  Email?: string;
  profile_image_url?: string;
  avatar?: string;
};

const formatAssignee = (assignee: any): Assignee => ({
  Name: assignee?.Name || assignee?.name || '',
  Id: assignee?.Id || assignee?.id || '',
  Email: assignee?.Email || assignee?.email,
  profile_image_url: assignee?.profile_image_url,
  avatar: assignee?.avatar || assignee?.profile_image_url,
});

const formatAssignees = (task: any): Assignee[] => {
  if (task.assignedTo && Array.isArray(task.assignedTo)) {
    const formatted = (task.assignedTo as Array<any>)
      .filter(
        (assignee: any) =>
          assignee && typeof assignee === 'object' && (assignee?.Id || assignee?.id),
      )
      .map(formatAssignee);
    if (formatted.length > 0) return formatted;
  }

  if (Array.isArray(task.assignedTo) && task.assignedTo.length > 0) {
    return task.assignedTo
      .filter((assignee: any) => assignee?.Id || assignee?.id)
      .map(formatAssignee);
  }

  if (typeof task.assignedTo === 'object' && task.assignedTo !== null && 'Id' in task.assignedTo) {
    return [formatAssignee(task.assignedTo)];
  }

  if (typeof task.assignedTo === 'string' && task.assignedTo) {
    return [{ Name: task.assignedToName || '', Id: task.assignedTo }];
  }

  if (task.assignTo?.Id) {
    return [formatAssignee(task.assignTo)];
  }

  return [];
};

const normalizeTask = (task: any, emptyTask: any, assignedTo?: any) => ({
  ...emptyTask,
  ...task,
  id: task.id || '',
  link: task.link || task.id || '',
  source: task.source || 'HUDDLE',
  subject: task.subject || task.name || '',
  name: task.subject || task.name || '',
  nameType: task.nameType || '',
  status: task.status || 'Not Started',
  priority: Array.isArray(task.priority) ? task.priority[0] : task.priority || 'Normal',
  dueDate: task.dueDate || task.date || '',
  assignedTo: task.assignedTo,
  createdDate: task.createdDate || task.createdAt || '',
  createdAt: task.createdAt || task.createdDate || '',
  description: task.description || '',
  isOverdue: task.isOverdue || false,
  relatedTo: task.relatedTo || '',
  relatedToType: task.relatedToType || '',
});

const emptyTask = {
  assignedTo: '',
  createdDate: '',
  decription: '',
  dueDate: '',
  isOverdue: true,
  link: '',
  name: '',
  nameType: '',
  priority: 'Normal',
  relatedTo: '',
  relatedToType: '',
  status: 'Not Started',
  subject: '',
  id: '',
  source: '',
};

export const TasksKanbanColumn = memo(
  ({
    data,
    loading,
    getTask,
    tabTitle,
    ellipse,
    setAction,
    setEdit,
    setViewDrawerState,
    viewDrawerState,
    onAddTask,
    onStatusChange,
    onDrag,
    onDragOver,
    onDrop,
  }: TasksKanbanColumnProps) => {
    const location = useLocation();
    const isTeamPath = location.pathname.includes('team');
    const taskFromOverview = sessionStorage.getItem('openTask') || '';
    const notesContent = sessionStorage.getItem('notes_content') || '';
    const notesId = sessionStorage.getItem('notes_id') || '';

    const [selected, setSelected] = useState(emptyTask);
    const [shouldEdit, setShouldEdit] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState<any>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const columnHeight = useMemo(() => {
      const isLargeScreen = window.innerWidth > 1700;
      if (isLargeScreen) {
        return isTeamPath ? 'calc(100vh - 360px)' : 'calc(100vh - 410px)';
      }
      return isTeamPath ? 'calc(100vh - 305px)' : 'calc(100vh - 340px)';
    }, [isTeamPath]);

    const handleTaskClick = useCallback(
      (task: any) => {
        let assignedToData: any = task.assignedTo || '';

        if (task.assignedTo && Array.isArray(task.assignedTo)) {
          const objectAssignees = (task.assignedTo as Array<any>).filter(
            (assignee: any) =>
              assignee && typeof assignee === 'object' && assignee?.Id && assignee?.Name,
          );
          if (objectAssignees.length > 0) {
            assignedToData = objectAssignees;
          }
        }

        setShouldEdit(false);
        setSelected(normalizeTask(task, emptyTask, assignedToData));
        setViewDrawerState(true);
      },
      [setViewDrawerState],
    );

    const handleEditTask = useCallback(
      (task: any) => {
        const formattedAssignees = formatAssignees(task);
        setSelected(
          normalizeTask(
            task,
            emptyTask,
            formattedAssignees.length > 0 ? formattedAssignees : task.assignedTo || [],
          ),
        );
        setShouldEdit(true);
        setViewDrawerState(true);
      },
      [setViewDrawerState],
    );

    const handleDeleteTask = useCallback((task: any) => {
      setTaskToDelete(task);
      setDeleteModalOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
      if (!taskToDelete?.id) return;

      setDeleteLoading(true);
      try {
        await TaskService.deleteTask(taskToDelete.id);
        setDeleteModalOpen(false);
        setTaskToDelete(null);
        setAction('removed');
        getTask();
      } catch (err: any) {
        console.error('Failed to delete task:', err);
      } finally {
        setDeleteLoading(false);
      }
    }, [taskToDelete?.id, getTask, setAction]);

    useEffect(() => {
      if (!viewDrawerState) {
        setShouldEdit(false);
      }
    }, [viewDrawerState]);

    const getAssigneeData = useCallback((task: any) => {
      let assignedToArray: Assignee[] | undefined;
      let assignedToId: string | undefined;
      let assignedToName: string | undefined;
      let avatar: string | undefined;

      if (task.assignedTo && Array.isArray(task.assignedTo)) {
        const objectAssignees = (task.assignedTo as Array<any>).filter(
          (assignee: any) =>
            assignee && typeof assignee === 'object' && assignee?.Id && assignee?.Name,
        );
        if (objectAssignees.length > 0) {
          assignedToArray = objectAssignees;
          assignedToId = objectAssignees[0]?.Id;
          assignedToName = objectAssignees[0]?.Name;
          avatar = objectAssignees[0]?.profile_image_url || objectAssignees[0]?.avatar;
        }
      }

      if (!assignedToArray || assignedToArray.length === 0) {
        if (Array.isArray(task.assignedTo) && task.assignedTo.length > 0) {
          assignedToArray = task.assignedTo;
          assignedToId = task.assignedTo[0]?.Id;
          assignedToName = task.assignedTo[0]?.Name;
          avatar = task.assignedTo[0]?.profile_image_url || task.assignedTo[0]?.avatar;
        } else if (
          typeof task.assignedTo === 'object' &&
          task.assignedTo !== null &&
          'Id' in task.assignedTo
        ) {
          assignedToArray = [task.assignedTo];
          assignedToId = task.assignedTo.Id;
          assignedToName = task.assignedTo.Name;
          avatar = task.assignedTo.avatar || task.assignedTo.profile_image_url;
        } else if (typeof task.assignedTo === 'string') {
          assignedToId = task.assignedTo;
          assignedToName = task.assignedTo;
        }
      }

      if (!assignedToName && task.assignedToName) {
        assignedToName = typeof task.assignedToName === 'string' ? task.assignedToName : undefined;
      }
      if (!avatar && task.avatar) {
        avatar = task.avatar;
      }

      return { assignedToArray, assignedToId, assignedToName, avatar };
    }, []);

    const handleStatusChange = useCallback(() => {
      onStatusChange ? onStatusChange() : getTask();
    }, [onStatusChange, getTask]);

    const handleUpdate = useCallback(() => {
      onStatusChange ? onStatusChange() : getTask();
    }, [onStatusChange, getTask]);

    const renderTaskCard = useCallback(
      (task: any) => {
        const { assignedToName, avatar } = getAssigneeData(task);

        return (
          <TaskCard
            key={task.id}
            data={{
              ...task,
              assignedTo: task.assignedTo,
              assignedToName,
              avatar,
            }}
            loading={loading}
            onClick={() => handleTaskClick(task)}
            onViewDetails={() => handleTaskClick(task)}
            onEdit={() => handleEditTask(task)}
            onStatusChange={handleStatusChange}
            onDelete={() => handleDeleteTask(task)}
            onDrag={onDrag}
          />
        );
      },
      [
        loading,
        handleTaskClick,
        handleEditTask,
        handleStatusChange,
        handleDeleteTask,
        onDrag,
        getAssigneeData,
      ],
    );

    useEffect(() => {
      if (taskFromOverview && data.records?.length) {
        const foundTask = data.records.find((task: any) => task.id === taskFromOverview);
        if (foundTask) {
          setSelected(foundTask);
          setViewDrawerState(true);
        }
      }
    }, [taskFromOverview, data.records, setViewDrawerState]);

    useEffect(() => {
      if ((notesContent || notesId) && !viewDrawerState) {
        setViewDrawerState(true);
      }
    }, [notesContent, notesId, viewDrawerState, setViewDrawerState]);

    const handleDragOver = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        onDragOver?.(e);
      },
      [onDragOver],
    );

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        onDrop?.(e, tabTitle);
      },
      [onDrop, tabTitle],
    );

    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '10px', xl: '16px' },
          height: '100%',
        }}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: { xs: '6px', xl: '12px' },
            flexShrink: 0,
          }}
        >
          {loading && data.totalFilteredRecords === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
              <Skeleton variant='circular' width={8} height={8} />
              <Skeleton variant='text' width={120} height={24} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              <Typography
                variant='responsiveHeading3'
                sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <img
                  src={`/assets/${ellipse}`}
                  width={8}
                  height={8}
                  alt={`${tabTitle} indicator`}
                />
                {tabTitle} ({data.totalFilteredRecords || 0})
                {loading && data.totalFilteredRecords > 0 && (
                  <CircularProgress
                    size={16}
                    sx={{ color: zeplinColor.BasicBlack, marginLeft: '8px' }}
                  />
                )}
              </Typography>
              {onAddTask && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: zeplinColor.Neutral4,
                    },
                    transition: 'background-color 0.2s',
                  }}
                  onClick={onAddTask}
                >
                  <img src='/assets/add.svg' alt='Add task' width={20} height={20} />
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {data.totalFilteredRecords <= 0 && loading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '10px', xl: '16px' },
                flex: 1,
                minHeight: 0,
              }}
            >
              {Array.from({ length: 9 }, (_, i) => (
                <TaskCard
                  key={i}
                  data={{}}
                  loading={true}
                  onClick={() => {}}
                  onViewDetails={() => {}}
                  onEdit={() => {}}
                  onStatusChange={() => {}}
                  onDelete={() => {}}
                />
              ))}
            </Box>
          ) : data.totalFilteredRecords <= 0 && !loading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1,
                minHeight: 0,
              }}
            >
              <img src='/assets/no-tasks.svg' className={styles['no-tasks-icon']} alt='No tasks' />
              <Typography
                variant='responsiveHeading3'
                color={zeplinColor.BasicBlack}
                sx={{ marginTop: '24px' }}
              >
                No Tasks Yet
              </Typography>
              <Typography
                variant='responsiveBody3'
                color={zeplinColor.Neutral8}
                sx={{ width: '70%', textAlign: 'center', marginTop: '12px' }}
              >
                Looks like there's nothing left to do. New task will appear here once added.
              </Typography>
            </Box>
          ) : (
            <InfiniteScrollColumn
              dataVal={data}
              render={(_item: any, _index: number) => null}
              height={columnHeight}
              paddingBottom='0rem'
              next={(offset) => getTask(offset)}
              total={data.totalFilteredRecords}
              loading={loading}
            >
              {() => <>{data.records.map((val: any) => renderTaskCard(val))}</>}
            </InfiniteScrollColumn>
          )}
        </Box>

        {viewDrawerState &&
          (selected.id || selected.link || selected.source || notesContent || notesId) && (
            <CustomViewTask
              setDrawerState={setViewDrawerState}
              drawerState={viewDrawerState}
              selectedData={selected}
              setDeleted={setAction}
              setEditDone={setEdit}
              setSelected={setSelected}
              isFromTasksTab={true}
              initialEditMode={shouldEdit}
              onUpdate={handleUpdate}
            />
          )}

        <ConfirmationModal
          open={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setTaskToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          loading={deleteLoading}
        />
      </Box>
    );
  },
);
